import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import ReactResizeDetector from 'react-resize-detector';
import {
  Box,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  Icon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  Typography,
  Link,
} from '@material-ui/core';

import { usePrevious } from '../../../../utils';
import ActionsCourse from '../../../../store/ducks/course';
import SectionCard from '../../../../components/SectionCard';

import Loading from './Loading';
import useStyles from './styles';

const Files = props => {
  const { onSectionChange } = props;
  const classes = useStyles();
  const dispatch = useDispatch();
  const { current, list } = useSelector(state => state.course);
  const [filesRequested, setFilesRequested] = useState(false);
  const [loading, setLoading] = useState(true);
  const [files, setFiles] = useState([]);
  const prevCurrent = usePrevious(current);

  const checkLessonChange = useCallback(
    (course, lesson) =>
      prevCurrent &&
      (course !== prevCurrent.course || lesson !== prevCurrent.lesson),
    [prevCurrent],
  );

  useEffect(() => {
    if (current) {
      const { course, lesson } = current;
      if (prevCurrent && checkLessonChange(course, lesson)) {
        setFilesRequested(false);
      }
    }
  }, [checkLessonChange, current, prevCurrent]);

  useEffect(() => {
    if (!loading) {
      const lessonChange = checkLessonChange(current?.course, current?.lesson);
      if (lessonChange) setLoading(true);
    }
  }, [current, loading, checkLessonChange]);

  useEffect(() => {
    if (!current || !list[current.course]?.courseDetailed) return;
    const hasCourse = current?.course in list;
    const hasLesson = current?.lesson in list[current.course]?.lessons;
    if (hasCourse && hasLesson) {
      const { course, lesson } = current;
      const lessonInfos = list[course].lessons[lesson];

      if (!lessonInfos.filesLoaded) {
        if (!filesRequested) {
          dispatch(ActionsCourse.requestLessonFiles(course, lesson));
          setFilesRequested(true);
        }
        if (!loading) {
          setLoading(true);
        }
      } else {
        setFiles(lessonInfos?.files);
        setLoading(false);
      }
    }
  }, [current, list, filesRequested, loading, dispatch]);

  useEffect(() => {
    const toRender = !!files.length;
    onSectionChange('render', toRender);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]);

  const handleFileSize = useCallback(
    size =>
      size > 999 ? `${parseInt(size / 1000, 10)}mb` : `${parseInt(size, 10)}kb`,
    [],
  );

  return loading ? (
    <SectionCard className="container-section">
      <Loading />
    </SectionCard>
  ) : (
    <>
      {files.length ? (
        <SectionCard
          className="container-section"
          id="files"
          title="Arquivos da aula"
        >
          <ReactResizeDetector
            handleHeight
            onResize={(width, height) => onSectionChange('height', height)}
          />
          <Box className={classes.container}>
            <List>
              {files.map((file, index, array) => (
                <Box key={file.fileName}>
                  <ListItem
                    disableGutters
                    classes={{ container: classes.itemContainer }}
                  >
                    <ListItemAvatar>
                      <Avatar className="file-avatar">
                        <Icon>insert_drive_file</Icon>
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle2" className="file-title">
                          {file.fileName}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" className="file-size">
                          {handleFileSize(file.fileSize)}
                        </Typography>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Link
                        underline="none"
                        target="_blank"
                        href={file.fileUrl}
                        download
                      >
                        <Typography
                          variant="caption"
                          className="file-btn-download-label"
                        >
                          Download
                        </Typography>
                        <IconButton edge="end">
                          <Icon>download</Icon>
                        </IconButton>
                      </Link>
                    </ListItemSecondaryAction>
                  </ListItem>
                  {index + 1 < array.length ? (
                    <Divider
                      variant="inset"
                      component="li"
                      classes={{ inset: classes.divider }}
                    />
                  ) : null}
                </Box>
              ))}
            </List>
          </Box>
        </SectionCard>
      ) : null}
    </>
  );
};

export default Files;
